import { listProjects } from '../apiService';
import { readSharedDeals } from './sharing';
import { classificationParentPath, ClassificationContext, ClassificationRequestError, readClassificationRegister } from './transport';

export type ClassificationHomeRow = {
  key: string; id: string; title: string; filename: string; status: string; pathCount: number; isProject: boolean;
  context: ClassificationContext; href: string; canShare: boolean;
};
export type ClassificationHomeData = { rows: ClassificationHomeRow[]; contexts: ClassificationContext[]; incomplete: boolean };

/** Read the actual Classification register, retaining each record's authorized scope. */
export async function readClassificationHome(actor: string, signal: AbortSignal, progress?: (data: ClassificationHomeData) => void): Promise<ClassificationHomeData> {
  const checkAbort = () => { if (signal.aborted) throw new DOMException('Classification load cancelled', 'AbortError'); };
  checkAbort();
  const sharedPromise = readSharedDeals(signal).then(deals => ({ deals, failed: false })).catch(() => ({ deals: [], failed: true }));
  const projects: string[] = [];
  let projectFailure = false;
  for (let page = 1; ; page++) {
    checkAbort();
    const result = await listProjects(actor, page, 100, 'active', true);
    checkAbort();
    if (!result.success || !result.data) { projectFailure = true; break; }
    const ids = result.data.projects.map(p => p.project_id);
    if (ids.some(id => !/^proj_[a-f0-9]{12}$/.test(id)) || (result.data.has_more && !ids.some(id => !projects.includes(id)))) {
      projectFailure = true; break;
    }
    projects.push(...ids.filter(id => !projects.includes(id)));
    if (!result.data.has_more) break;
  }
  const rows = new Map<string, ClassificationHomeRow>();
  const contexts: ClassificationContext[] = [];
  let incomplete = projectFailure;
  const snapshot = () => ({ rows: Array.from(rows.values()).sort((a, b) => a.title.localeCompare(b.title)), contexts: [...contexts], incomplete });
  const publish = () => { checkAbort(); progress?.(snapshot()); };
  const run = async (tasks: Array<{ projectId: string; share?: string }>) => {
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(4, tasks.length) }, async () => {
      while (next < tasks.length) {
        checkAbort();
        const task = tasks[next++];
        try {
          const { context, projects: records } = await readClassificationRegister(task.projectId, signal, task.share);
          if (!Array.isArray(records)) throw Error('Invalid Classification register');
          const loaded = records.map(value => {
            if (!value || typeof value !== 'object' || Array.isArray(value)) throw Error('Invalid Classification project');
            const record = value as Record<string, unknown>;
            if (!record || typeof record.contract_id !== 'string' || !/^[a-f0-9]{32}$/.test(record.contract_id) || typeof record.filename !== 'string'
              || typeof record.title !== 'string') throw Error('Invalid Classification project');
            if (context.deal_scope && record.contract_id !== context.deal_scope.root_contract_id) throw Error('Invalid shared Classification project');
            const id = record.contract_id;
            return { key: JSON.stringify([context.company_slug, context.owner_identity, context.project_id, id]), id, title: record.title, filename: record.filename, status: typeof record.status === 'string' ? record.status : 'In progress',
              pathCount: typeof record.path_count === 'number' && Number.isSafeInteger(record.path_count) && record.path_count >= 0 ? record.path_count : 0,
              isProject: record.is_project === true, context, canShare: record.can_share === true,
              href: classificationParentPath(context, new URLSearchParams({ page: 'standalone.html', view: 'contract', instrument: `contract:${id}` }).toString()) };
          });
          checkAbort();
          contexts.push(context);
          for (const row of loaded) {
            if (!rows.has(row.key) || !context.deal_scope) rows.set(row.key, row);
          }
          publish();
        } catch (error) {
          checkAbort();
          // Inaccessible generic BARK projects are not Classification content.
          if (!(error instanceof ClassificationRequestError) || ![403, 404].includes(error.status)) incomplete = true;
        }
      }
    }));
  };
  await Promise.all([run(projects.map(projectId => ({ projectId }))), sharedPromise.then(async shared => {
    incomplete = incomplete || shared.failed;
    await run(shared.deals.map(deal => ({ projectId: deal.project_id, share: deal.share_id })));
    publish();
  })]);
  checkAbort();
  return snapshot();
}
